// System-map inspect panel (wave 2 G2) — one chrome controller owning the
// four graph-query features that share the engine's highlight primitive
// (G1): Node Finder, route probe, reachability and the semantic lens. Lives
// inside a PanelComponent composition (InspectPanelComponent provides the
// markup); kept OUT of the engine — the panel is a host of the public
// engine API (getNodes/getEdges/selectNode/setCenter/highlight), exactly
// like the palette filter owns its own one responsibility (PR #57 pattern).
//
// ARCHIFY GROUNDING RULE (binding): every graph query here is a BFS over the
// engine's AUTHORED data only — getNodes()/getEdges() def source/target
// keys. No DOM geometry, no computed positions, no transitive inference ever
// participates in connectivity. Equal-length route ties break by authored
// edge array order (getEdges() returns the engine map in insertion order),
// so every query is deterministic. Unreachable targets produce an explicit
// "no route" state, never a guess.
//
// ONE BOUNDED OWNER: the four features share engine.highlight() — the last
// feature whose control the user touched owns the highlight, and claiming
// ownership resets the other three's UI state (search text, route selects,
// reach direction, lens choice). The canvas shows one query's answer at a
// time.
//
// URL-STATE OWNERSHIP (wave 2 split — each hash param has exactly ONE
// writer): this controller is the designated owner of the #route=/#lens=
// round-trips; ?present=/#focus= belong to ui--flow-url-state and
// #view=/#beat= to ui--flow-story. Wave 3 lands the #lens= round-trip
// (written on every lens change, restored at boot — replaceState only,
// every other hash param preserved). Wave 4 lands the #route= round-trip:
// written ONLY while a full route is active (both endpoints picked, path
// highlighted) — the source-only step is transient and every return to it
// deletes the param — and restored at boot with full validation (exactly
// two ids, both known nodes, a directed path between them) or normalized
// to deletion. Same replaceState discipline, same foreign-param
// preservation as #lens= (the two writers round-trip through
// URLSearchParams, so each preserves the other's param).
//
// Escape ladder (panel-local keydown, attached in connect — the dnd-node
// keydown precedent, so no keydown action string in the markup): route
// steps path → source → idle; the other owners disengage in one step.
//
// Route/lens option lists populate ONCE at connect from getNodes() — a host
// that mutates the graph mid-session re-renders the panel (v1; the panel is
// a map-inspection surface over a stable map, not a live editor chrome).
import { Controller } from "@hotwired/stimulus"

// Lens chip styling (UX audit — the chips group replaced the select[
// multiple]): mirrors InspectPanelComponent's lens_chip_selected_classes /
// lens_chip_classes so JS-rebuilt chips match the SSR sentinel verbatim.
const LENS_CHIP_SELECTED = "rounded-full border border-primary bg-primary/10 px-2 py-0.5 text-xs text-primary"
const LENS_CHIP_UNSELECTED = "rounded-full border border-border bg-card px-2 py-0.5 text-xs text-foreground-medium hover:bg-muted"

export default class extends Controller {
  static targets = [
    "searchInput", "searchResults",
    "routeSource", "routeTarget", "routeStatus",
    "reachFrom", "reachUpstream", "reachDownstream", "reachStatus",
    "lensSelect"
  ]
  static outlets = ["ui--flow"]

  connect() {
    this._owner = null           // null | "search" | "route" | "reach" | "lens"
    this._routeState = "idle"    // idle | source | path
    this._routeSourceId = null
    this._reachable = null       // Set of node ids reachable from the route source
    this._reachDirection = null  // null | "upstream" | "downstream"
    this._reachNodeId = null
    this._lensTypes = []         // ordered active lens types (max 2 — a third pick drops the oldest)

    // Cmd/Ctrl+F (the engine's reserved searchrequest hook) focuses the
    // finder. Multi-canvas: only answer the event from THIS panel's engine —
    // the event target is the dispatching canvas element.
    this._onSearchRequest = (event) => {
      if (event.target !== this._engine()?.element) return
      if (!this.hasSearchInputTarget) return
      this.searchInputTarget.focus()
      this.searchInputTarget.select()
    }
    // Reach works "from the currently selected node": track selectionchange
    // for the from-label + button enablement, and recompute a live closure
    // while reach owns the highlight (click another node mid-inspection and
    // the closure follows).
    this._onSelectionChange = (event) => {
      if (event.target !== this._engine()?.element) return
      this._syncReachFrom(event.detail?.nodes || [])
    }
    this._onKeydown = (event) => { if (event.key === "Escape") this._escapeStep() }
    document.addEventListener("ui--flow:searchrequest", this._onSearchRequest)
    document.addEventListener("ui--flow:selectionchange", this._onSelectionChange)
    this.element.addEventListener("keydown", this._onKeydown)

    this._populateRouteSources()
    this._populateLens()
    this._renderRouteTargets()
    this._syncReachFrom(this._engine() ? [...this._engine().selectedNodes] : [])
    this._bootLensFromHash()
    this._restoreRouteFromHash()
  }

  disconnect() {
    document.removeEventListener("ui--flow:searchrequest", this._onSearchRequest)
    document.removeEventListener("ui--flow:selectionchange", this._onSelectionChange)
    this.element.removeEventListener("keydown", this._onKeydown)
  }

  _engine() { return this.hasUiFlowOutlet ? this.uiFlowOutlet : null }
  _nodes() { return this._engine()?.getNodes() || [] }
  _edges() { return this._engine()?.getEdges() || [] }

  // Selector-safe id interpolation (mirror of the engine's
  // _escapeSelectorValue — ids arrive from host data, so quotes/backslashes
  // must not break the querySelector).
  _escape(value) {
    const raw = `${value ?? ""}`
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(raw)
    return raw.replace(/(["\\])/g, "\\$1")
  }

  _nodeLabel(node) { return `${node.label ?? node.data?.label ?? node.id}` }

  // ── Node Finder ─────────────────────────────────────────
  // Case-insensitive substring over label + type + id, results in authored
  // (getNodes) order. Typing claims the highlight for the matches; an empty
  // query disengages; ZERO matches clear rather than dim the world (the
  // engine's empty-set guard would no-op anyway — a stale highlight from the
  // previous keystroke must not linger).

  search() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.trim().toLowerCase() : ""
    const matches = query ? this._nodes().filter(n => this._nodeMatches(n, query)) : []
    this._renderSearchResults(matches, query)
    if (!query) {
      if (this._owner === "search") { this._owner = null; this._engine()?.clearHighlight() }
      return
    }
    this._claim("search")
    if (matches.length) this._engine()?.highlight({ nodes: matches.map(n => n.id) })
    else this._engine()?.clearHighlight()
  }

  _nodeMatches(node, query) {
    return this._nodeLabel(node).toLowerCase().includes(query) ||
      `${node.type || "default"}`.toLowerCase().includes(query) ||
      `${node.id}`.toLowerCase().includes(query)
  }

  _renderSearchResults(matches, query) {
    if (!this.hasSearchResultsTarget) return
    const list = this.searchResultsTarget
    list.textContent = ""
    if (!query) return
    if (matches.length === 0) {
      const empty = document.createElement("p")
      empty.className = "px-2 py-1 text-xs text-foreground-medium"
      empty.textContent = "No nodes match"
      list.appendChild(empty)
      return
    }
    matches.forEach(node => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "block w-full cursor-pointer rounded px-2 py-1 text-left text-xs text-foreground hover:bg-muted"
      item.dataset.nodeId = node.id
      // label — type · id (all three columns the finder searches over).
      const label = document.createElement("span")
      label.className = "font-medium"
      label.textContent = this._nodeLabel(node)
      const meta = document.createElement("span")
      meta.className = "text-foreground-medium"
      meta.textContent = ` — ${node.type || "default"} · ${node.id}`
      item.appendChild(label)
      item.appendChild(meta)
      item.addEventListener("click", () => this._chooseSearchResult(node.id))
      list.appendChild(item)
    })
  }

  // Click → select the node + center the viewport on it. fitView has no
  // per-node variant — the center is the node's flow-space position (public
  // flowPosition, sub-flow aware) plus its measured box, at the finder's
  // fixed 1.5 zoom.
  _chooseSearchResult(id) {
    const engine = this._engine()
    if (!engine) return
    engine.selectNode(id)
    const el = engine.element.querySelector(`[data-ui--flow-node="${this._escape(id)}"]`)
    if (!el) return
    const pos = engine.flowPosition(el)
    engine.setCenter(pos.x + el.offsetWidth / 2, pos.y + el.offsetHeight / 2, 1.5)
  }

  // ── Route probe ─────────────────────────────────────────
  // Pick a source → BFS marks the reachable candidates (canvas dim + the
  // target select disables the unreachable) → pick a target → shortest
  // directed path highlights with a hop count. source==target is rejected
  // (the source never lists itself as a target); an unreachable target (only
  // reachable by driving the API directly — the select disables them) gets
  // the explicit "no route" state.

  routeSourceChanged() {
    const id = this.hasRouteSourceTarget ? this.routeSourceTarget.value : ""
    if (!id) {
      if (this._owner === "route") { this._owner = null; this._engine()?.clearHighlight() }
      this._resetRoute()
      return
    }
    this._claim("route")
    this._routeSourceId = id
    const seen = this._bfs(id, "downstream")
    seen.delete(id)
    this._reachable = new Set(seen.keys())
    this._routeState = "source"
    this._renderRouteTargets()
    this._markRouteCandidates()
    this._writeRouteHash() // source-only is transient — any stale #route= dies here
  }

  routeTargetChanged() {
    const targetId = this.hasRouteTargetTarget ? this.routeTargetTarget.value : ""
    if (!this._routeSourceId) { this._resetRoute(); return }
    if (!targetId) {
      // Cleared the target → back to the source step's candidate marking.
      this._routeState = "source"
      this._markRouteCandidates()
      this._writeRouteHash() // back to transient — the param dies with the path
      return
    }
    this._claim("route")
    const source = this._nodes().find(n => n.id === this._routeSourceId)
    const target = this._nodes().find(n => n.id === targetId)
    const path = this._shortestPath(this._routeSourceId, targetId)
    if (!path) {
      // Explicit no-route state: the candidate marking from the source step
      // stays (it IS the truthful reachability picture) and no path state is
      // entered — Escape then steps straight back to idle.
      this._routeState = "source"
      this._setRouteStatus(`No route ${source ? this._nodeLabel(source) : this._routeSourceId} → ${target ? this._nodeLabel(target) : targetId}`)
      this._writeRouteHash() // no path → no param
      return
    }
    this._routeState = "path"
    this._engine()?.highlight({ nodes: path.nodes, edges: path.edges })
    const hops = path.edges.length
    const labels = path.nodes.map(id => {
      const n = this._nodes().find(node => node.id === id)
      return n ? this._nodeLabel(n) : id
    })
    this._setRouteStatus(`${hops} ${hops === 1 ? "hop" : "hops"} — ${labels.join(" → ")}`)
    this._writeRouteHash() // the ONE write: a full route is active
  }

  // The source step's candidate marking: light the source + everything
  // reachable (and the edges among them), dim the rest — "BFS marks
  // reachable candidates" made visible.
  _markRouteCandidates() {
    if (!this._routeSourceId || !this._reachable) return
    const lit = new Set([this._routeSourceId, ...this._reachable])
    this._engine()?.highlight({ nodes: [...lit], edges: this._edgesWithin(lit) })
    this._setRouteStatus(this._reachable.size
      ? `${this._reachable.size} reachable — pick a target`
      : "No route from here")
  }

  // Shortest directed path source → target: BFS discovery order is the
  // authored edge order, so equal-length ties resolve deterministically.
  _shortestPath(sourceId, targetId) {
    if (sourceId === targetId) return null
    const seen = this._bfs(sourceId, "downstream")
    if (!seen.has(targetId)) return null
    const nodes = [targetId]
    const edges = []
    let cur = targetId
    while (cur !== sourceId) {
      const step = seen.get(cur)
      edges.unshift(step.viaEdgeId)
      nodes.unshift(step.viaNodeId)
      cur = step.viaNodeId
    }
    return { nodes, edges }
  }

  // #route=<src>,<dst> boot restore (wave 4): BOTH ids must be real nodes
  // AND a directed path must exist between them — then the user flow
  // replays verbatim through the public handlers (source candidates, then
  // the path highlight + status readout). Anything less — an unknown id,
  // an unreachable pair, not-exactly-two ids, garbage — deletes the param
  // and stays idle. Percent-encoded and literal-comma forms parse
  // identically (URLSearchParams, the lens pins' equivalence). The owner
  // is pre-set so the replay's _claim("route") no-ops: a co-booted #lens=
  // restore (which likewise set its owner directly) survives — the two
  // writers preserve each other's params.
  _restoreRouteFromHash() {
    const raw = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("route")
    if (!raw) return
    const ids = raw.split(",").map(id => id.trim())
    const [sourceId, targetId] = ids
    const known = new Set(this._nodes().map(n => n.id))
    const valid = ids.length === 2 && Boolean(sourceId) && Boolean(targetId) &&
      known.has(sourceId) && known.has(targetId) &&
      Boolean(this._shortestPath(sourceId, targetId))
    if (!valid) { this._writeRouteHash(); return } // normalize to deletion
    this._owner = "route"
    if (this.hasRouteSourceTarget) {
      this.routeSourceTarget.value = sourceId
      this.routeSourceChanged()
    }
    if (this.hasRouteTargetTarget) {
      this.routeTargetTarget.value = targetId
      this.routeTargetChanged()
    }
  }

  // The route hash round-trip — this controller's second owned param (see
  // the header ownership table). Written ONLY while a full route is active
  // (_routeState === "path"); every other route state deletes the param.
  // replaceState only; every other hash param (#lens=, #view=, #beat=,
  // #focus=, host params) survives untouched.
  _writeRouteHash() {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    const targetId = this.hasRouteTargetTarget ? this.routeTargetTarget.value : ""
    if (this._routeState === "path" && this._routeSourceId && targetId) {
      params.set("route", `${this._routeSourceId},${targetId}`)
    } else {
      params.delete("route")
    }
    const hash = params.toString()
    const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`
    window.history.replaceState(null, "", url)
  }

  // ── Reachability ────────────────────────────────────────
  // One click per direction from the currently selected node: upstream walks
  // authored edges to→from, downstream from→to. The closure is the induced
  // subgraph (every authored edge with both endpoints in the closure), with
  // node/link counts and the max hop depth. Re-clicking the active direction
  // toggles it off.

  reachUpstream() { this._toggleReach("upstream") }
  reachDownstream() { this._toggleReach("downstream") }

  _toggleReach(direction) {
    if (this._owner === "reach" && this._reachDirection === direction) {
      this._reachDirection = null
      this._owner = null
      this._engine()?.clearHighlight()
      this._syncReachButtons()
      this._setReachStatus("")
      return
    }
    this._claim("reach")
    this._reachDirection = direction
    this._applyReach()
  }

  _applyReach() {
    const engine = this._engine()
    if (!engine || !this._reachDirection) return
    if (!this._reachNodeId) {
      // Anchorless while armed (the selection was cleared mid-inspection):
      // dim nothing, keep the direction — the next selection recomputes.
      engine.clearHighlight()
      this._setReachStatus("Select a node on the canvas")
      this._syncReachButtons()
      return
    }
    const seen = this._bfs(this._reachNodeId, this._reachDirection)
    seen.delete(this._reachNodeId)
    const lit = new Set([this._reachNodeId, ...seen.keys()])
    const edges = this._edgesWithin(lit)
    engine.highlight({ nodes: [...lit], edges })
    const maxHops = Math.max(0, ...[...seen.values()].map(v => v.depth))
    this._setReachStatus(`${seen.size} ${seen.size === 1 ? "node" : "nodes"} · ${edges.length} ${edges.length === 1 ? "link" : "links"} · max ${maxHops} ${maxHops === 1 ? "hop" : "hops"}`)
    this._syncReachButtons()
  }

  // selectionchange → the reach "from" follows the first selected node. A
  // live closure recomputes on every change (click another node mid-
  // inspection and the closure follows; an emptied selection pauses it —
  // the direction stays armed until a re-click or Escape disengages).
  _syncReachFrom(nodeIds) {
    this._reachNodeId = nodeIds[0] || null
    const node = this._reachNodeId ? this._nodes().find(n => n.id === this._reachNodeId) : null
    if (this.hasReachFromTarget) {
      this.reachFromTarget.textContent = node ? `From: ${this._nodeLabel(node)}` : "Select a node on the canvas"
    }
    this._syncReachButtons()
    if (this._owner === "reach" && this._reachDirection) this._applyReach()
  }

  _syncReachButtons() {
    const enable = Boolean(this._reachNodeId)
    if (this.hasReachUpstreamTarget) {
      this.reachUpstreamTarget.disabled = !enable
      this.reachUpstreamTarget.classList.toggle("bg-primary/10", this._reachDirection === "upstream")
    }
    if (this.hasReachDownstreamTarget) {
      this.reachDownstreamTarget.disabled = !enable
      this.reachDownstreamTarget.classList.toggle("bg-primary/10", this._reachDirection === "downstream")
    }
  }

  // ── Semantic lens ───────────────────────────────────────
  // Up to TWO types at a time (wave 3 — the two-kind cross-match parked at
  // the wave-2 spec): the chips group (UX audit — it replaced the select[
  // multiple]) lists the graph's node types count-sorted with the counts in
  // the chip labels; every pick highlights the UNION of the active types'
  // nodes (a union of two authored type-membership sets over getNodes() —
  // no new query class). Picking a third type replaces the OLDEST (the
  // active pair is an ordered two-slot history); picking "All types" (or
  // re-clicking the last active chip) clears. The pair round-trips through
  // #lens=a,b — a single id stays backward compatible, unknown types are
  // dropped and the hash caps at the first two ids.

  lensChipClicked(event) {
    const value = event.currentTarget.dataset.lensValue ?? ""
    if (value === "") {
      // "All types" sentinel — the clear empties both slots.
      this._lensTypes = []
      this._syncLensSelect()
      if (this._owner === "lens") { this._owner = null; this._engine()?.clearHighlight() }
      this._writeLensHash()
      return
    }
    if (this._lensTypes.includes(value)) {
      // Re-clicking an active chip deselects it (the clear path when it was
      // the last one).
      this._lensTypes = this._lensTypes.filter(v => v !== value)
    } else {
      // Append and let the two-slot cap drop the oldest.
      this._lensTypes = [...this._lensTypes, value].slice(-2)
    }
    this._syncLensSelect()
    if (this._lensTypes.length === 0) {
      if (this._owner === "lens") { this._owner = null; this._engine()?.clearHighlight() }
    } else {
      this._claim("lens")
      this._applyLens()
    }
    this._writeLensHash()
  }

  // Union of the active types' node ids — authored getNodes() data only.
  _applyLens() {
    const active = new Set(this._lensTypes)
    const ids = this._nodes().filter(n => active.has(n.type || "default")).map(n => n.id)
    this._engine()?.highlight({ nodes: ids })
  }

  // The chips mirror the ordered pair exactly: aria-pressed + the selected/
  // unselected pill classes; the sentinel shows selected while the lens is
  // off (the pair lives here, not in the DOM).
  _syncLensSelect() {
    if (!this.hasLensSelectTarget) return
    this.lensSelectTarget.querySelectorAll("[data-lens-value]").forEach(chip => {
      const value = chip.dataset.lensValue ?? ""
      const selected = value === "" ? this._lensTypes.length === 0 : this._lensTypes.includes(value)
      chip.setAttribute("aria-pressed", selected ? "true" : "false")
      chip.className = selected ? LENS_CHIP_SELECTED : LENS_CHIP_UNSELECTED
    })
  }

  // #lens=<a>[,<b>] boot restore: unknown types drop (the highlight
  // unknown-id guard idiom), the pair caps at the first two, and the hash
  // is normalized back (a garbage-only param deletes itself).
  _bootLensFromHash() {
    const raw = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("lens")
    if (!raw) return
    const known = new Set(this._nodes().map(n => n.type || "default"))
    const types = raw.split(",").map(t => t.trim()).filter(t => t && known.has(t)).slice(0, 2)
    if (types.length === 0) { this._writeLensHash(); return }
    this._lensTypes = types
    this._syncLensSelect()
    this._owner = "lens"
    this._applyLens()
    this._writeLensHash()
  }

  // The lens hash round-trip — one of this controller's two owned params
  // (see the header ownership table). replaceState only; every other hash
  // param (#route=, #view=, #beat=, #focus=, host params) survives
  // untouched.
  _writeLensHash() {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""))
    if (this._lensTypes.length) params.set("lens", this._lensTypes.join(","))
    else params.delete("lens")
    const hash = params.toString()
    const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`
    window.history.replaceState(null, "", url)
  }

  _populateLens() {
    if (!this.hasLensSelectTarget) return
    const counts = new Map()
    this._nodes().forEach(n => {
      const type = n.type || "default"
      counts.set(type, (counts.get(type) || 0) + 1)
    })
    // Count-sorted (desc), name asc on ties — deterministic.
    const types = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    const group = this.lensSelectTarget
    group.textContent = ""
    group.appendChild(this._lensChip("", "All types"))
    types.forEach(([type, count]) => group.appendChild(this._lensChip(type, `${type} (${count})`)))
    this._syncLensSelect()
  }

  _lensChip(value, label) {
    const chip = document.createElement("button")
    chip.type = "button"
    chip.dataset.lensValue = value
    chip.textContent = label
    chip.setAttribute("aria-pressed", "false")
    chip.className = LENS_CHIP_UNSELECTED
    chip.addEventListener("click", (event) => this.lensChipClicked(event))
    return chip
  }

  // ── Shared graph reads (authored data only) ─────────────

  // Directed adjacency from getEdges() defs ONLY (the archify rule — never
  // DOM, never geometry). Self-loops join no adjacency list (spec: excluded).
  // Neighbor order = authored edge array order — the deterministic tie-break.
  _adjacency(direction) {
    const adj = new Map()
    for (const e of this._edges()) {
      if (e.source === e.target) continue
      const from = direction === "upstream" ? e.target : e.source
      const to = direction === "upstream" ? e.source : e.target
      if (!adj.has(from)) adj.set(from, [])
      adj.get(from).push({ to, edgeId: e.id })
    }
    return adj
  }

  // BFS from startId over authored adjacency; returns id → {depth,
  // viaEdgeId, viaNodeId} in discovery order (the start included at depth 0).
  _bfs(startId, direction) {
    const adj = this._adjacency(direction)
    const seen = new Map([[startId, { depth: 0, viaEdgeId: null, viaNodeId: null }]])
    const queue = [startId]
    while (queue.length) {
      const cur = queue.shift()
      const depth = seen.get(cur).depth
      for (const { to, edgeId } of adj.get(cur) || []) {
        if (seen.has(to)) continue
        seen.set(to, { depth: depth + 1, viaEdgeId: edgeId, viaNodeId: cur })
        queue.push(to)
      }
    }
    return seen
  }

  // Every authored edge with BOTH endpoints in the set (the induced subgraph
  // — what "highlight the closure/candidates" means for links).
  _edgesWithin(idSet) {
    return this._edges().filter(e => idSet.has(e.source) && idSet.has(e.target)).map(e => e.id)
  }

  // ── One bounded owner ───────────────────────────────────

  _claim(owner) {
    if (this._owner === owner) return
    this._owner = owner
    if (owner !== "search") this._resetSearch()
    if (owner !== "route") this._resetRoute()
    if (owner !== "reach") this._resetReach()
    if (owner !== "lens") this._resetLens()
  }

  _resetSearch() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._renderSearchResults([], "")
  }

  _resetRoute() {
    this._routeState = "idle"
    this._routeSourceId = null
    this._reachable = null
    if (this.hasRouteSourceTarget) this.routeSourceTarget.value = ""
    this._renderRouteTargets()
    this._setRouteStatus("Pick a source")
    this._writeRouteHash() // idle owns no hash param (also the other-claimant path)
  }

  _resetReach() {
    this._reachDirection = null
    this._syncReachButtons()
    this._setReachStatus("")
  }

  _resetLens() {
    this._lensTypes = []
    this._syncLensSelect()
    this._writeLensHash()
  }

  // Escape: route steps path → source → idle; the other owners disengage.
  _escapeStep() {
    if (this._owner === "route") {
      if (this._routeState === "path") {
        this._routeState = "source"
        if (this.hasRouteTargetTarget) this.routeTargetTarget.value = ""
        this._markRouteCandidates()
        this._writeRouteHash() // path → source: the param dies with the path
      } else {
        this._resetRoute()
        this._owner = null
        this._engine()?.clearHighlight()
      }
    } else if (this._owner) {
      const owner = this._owner
      this._owner = null
      if (owner === "search") this._resetSearch()
      if (owner === "reach") this._resetReach()
      if (owner === "lens") this._resetLens()
      this._engine()?.clearHighlight()
    }
  }

  // ── Selects & status lines ──────────────────────────────

  _populateRouteSources() {
    if (!this.hasRouteSourceTarget) return
    const select = this.routeSourceTarget
    select.textContent = ""
    select.appendChild(new Option("Pick a source…", ""))
    this._nodes().forEach(n => select.appendChild(new Option(this._nodeOptionLabel(n), n.id)))
  }

  // Before a source: a disabled placeholder. After: every OTHER node —
  // reachable ones plain, unreachable disabled with the explicit "no route"
  // marker (never silently absent).
  _renderRouteTargets() {
    if (!this.hasRouteTargetTarget) return
    const select = this.routeTargetTarget
    select.textContent = ""
    if (!this._routeSourceId || !this._reachable) {
      select.appendChild(new Option("Pick a source first", ""))
      select.disabled = true
      return
    }
    select.disabled = false
    select.appendChild(new Option("Pick a target…", ""))
    this._nodes().forEach(n => {
      if (n.id === this._routeSourceId) return
      const reachable = this._reachable.has(n.id)
      const option = new Option(reachable ? this._nodeOptionLabel(n) : `${this._nodeOptionLabel(n)} (no route)`, n.id)
      option.disabled = !reachable
      select.appendChild(option)
    })
  }

  _nodeOptionLabel(node) {
    const label = this._nodeLabel(node)
    return label === `${node.id}` ? `${node.id}` : `${label} (${node.id})`
  }

  _setRouteStatus(text) { if (this.hasRouteStatusTarget) this.routeStatusTarget.textContent = text }
  _setReachStatus(text) { if (this.hasReachStatusTarget) this.reachStatusTarget.textContent = text }
}
